import React, { useState } from "react";
import { Alert, Button, Card, Checkbox, Col, Radio, Row, Space, Typography } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, QuestionCircleOutlined, SyncOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const SurveyQuizBlock = ({ surveyQuiz, onSkip, onUpdateToken, onSubmit }) => {
    const [answers, setAnswers] = useState({});
    const questions = Array.isArray(surveyQuiz?.questions) ? surveyQuiz.questions : [];
    const allAnswered = questions.length > 0 && questions.every((item) => {
        const answer = answers[item.question_id];
        return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null;
    });
    const isProcessing = surveyQuiz?.processing === true;
    const isSkippable = surveyQuiz?.isSkippable === true || surveyQuiz?.isSkippable === "1";
    const canSkip = isSkippable && Number(surveyQuiz?.snoozedCount) <= Number(surveyQuiz?.snoozeLimit);

    const handleAnswerChange = (questionId, answerId) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: answerId,
        }));
    };

    const isCheckboxQuestion = (questionType) => ["checkbox-image", "checkbox-text"].includes(questionType);

    const getAnswerType = (questionType) => isCheckboxQuestion(questionType) ? "checkbox" : "radio";

    const handleCheckboxAnswerChange = (questionId, answerId, checked) => {
        setAnswers((prev) => {
            const currentAnswers = Array.isArray(prev[questionId]) ? prev[questionId] : [];
            return {
                ...prev,
                [questionId]: checked
                    ? [...currentAnswers, answerId]
                    : currentAnswers.filter((item) => item !== answerId),
            };
        });
    };

    const getQuizData = () => {
        return questions.map((question) => {
            const answerType = getAnswerType(question.question_type);
            const selectedAnswer = answers[question.question_id];
            const quizAnswer = Array.isArray(selectedAnswer)
                ? selectedAnswer.join(",")
                : selectedAnswer;

            return {
                quizQuestionId: Number(question.question_id),
                quizAnswer: String(quizAnswer),
                quizquestionType: answerType,
            };
        });
    };

    return (
        <div
            style={{
                minHeight: "calc(100vh - 64px)",
                background: "#f4f7fb",
                padding: "32px 16px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
            }}
        >
            <Card
                styles={{ body: { padding: 0 } }}
                style={{
                    width: "100%",
                    maxWidth: 960,
                    borderRadius: 12,
                    border: "1px solid #d8e3f0",
                    boxShadow: "0 16px 40px rgba(18, 52, 86, 0.12)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        padding: "24px 28px",
                        background: "#ffffff",
                        borderBottom: "1px solid #e6edf5",
                    }}
                >
                    <Space align="start" size={14}>
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 12,
                                background: "#e8f1fb",
                                color: "#1160A9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 22,
                            }}
                        >
                            {isProcessing ? <ClockCircleOutlined /> : <QuestionCircleOutlined />}
                        </div>
                        <div>
                            <Title level={4} style={{ margin: 0, color: "#123456" }}>
                                Store Quiz
                            </Title>
                            <Text style={{ color: "#5b6b7d" }}>
                                {surveyQuiz?.displayMesssage || surveyQuiz?.message || "Please complete the quiz to access the portal."}
                            </Text>
                        </div>
                    </Space>
                </div>

                <div style={{ padding: "24px 28px" }}>
                    {surveyQuiz?.error && (
                        <Alert
                            type="error"
                            showIcon
                            message={surveyQuiz.error}
                            style={{ borderRadius: 10, marginBottom: 16 }}
                        />
                    )}

                    {isProcessing ? (
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <Alert
                                type="warning"
                                showIcon
                                message="Quiz Already Processing"
                                description={surveyQuiz?.displayMesssage || "Another quiz process is already running."}
                                style={{ borderRadius: 10 }}
                            />
                            <div
                                style={{
                                    border: "1px solid #f7d7a5",
                                    borderRadius: 10,
                                    background: "#fffaf0",
                                    padding: 18,
                                }}
                            >
                                <Row gutter={[16, 16]} align="middle" justify="space-between">
                                    <Col xs={24} md={16}>
                                        <Text strong style={{ color: "#92400e" }}>
                                            Do you want to clear the other process and start the quiz here?
                                        </Text>
                                    </Col>
                                    <Col xs={24} md={8} style={{ textAlign: "right" }}>
                                        <Button
                                            type="primary"
                                            icon={<SyncOutlined />}
                                            loading={surveyQuiz?.updateTokenLoading}
                                            onClick={onUpdateToken}
                                        >
                                            Clear and Continue
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </Space>
                    ) : (
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            {questions.map((question, index) => (
                                <div
                                    key={question.question_id}
                                    style={{
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 10,
                                        background: "#ffffff",
                                        padding: 18,
                                    }}
                                >
                                    <Row gutter={[16, 16]}>
                                        <Col span={24}>
                                            <Text strong style={{ color: "#1f2937", fontSize: 14 }}>
                                                {index + 1}. {question.question_text}
                                            </Text>
                                        </Col>
                                        <Col span={24}>
                                            {isCheckboxQuestion(question.question_type) ? (
                                                <Row gutter={[12, 12]}>
                                                    {(question.answer_list || []).map((answer) => {
                                                        const checked = Array.isArray(answers[question.question_id]) && answers[question.question_id].includes(answer.answer_id);
                                                        return (
                                                            <Col xs={24} sm={12} key={answer.answer_id}>
                                                                <Checkbox
                                                                    checked={checked}
                                                                    onChange={(event) => handleCheckboxAnswerChange(question.question_id, answer.answer_id, event.target.checked)}
                                                                    style={{
                                                                        width: "100%",
                                                                        border: "1px solid #d9e2ec",
                                                                        borderRadius: 8,
                                                                        padding: "10px 12px",
                                                                        margin: 0,
                                                                        background: checked ? "#eef6ff" : "#ffffff",
                                                                    }}
                                                                >
                                                                    {answer.answer_text}
                                                                </Checkbox>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            ) : (
                                                <Radio.Group
                                                    value={answers[question.question_id]}
                                                    onChange={(event) => handleAnswerChange(question.question_id, event.target.value)}
                                                    style={{ width: "100%" }}
                                                >
                                                    <Row gutter={[12, 12]}>
                                                        {(question.answer_list || []).map((answer) => (
                                                            <Col xs={24} sm={12} key={answer.answer_id}>
                                                                <Radio
                                                                    value={answer.answer_id}
                                                                    style={{
                                                                        width: "100%",
                                                                        border: "1px solid #d9e2ec",
                                                                        borderRadius: 8,
                                                                        padding: "10px 12px",
                                                                        margin: 0,
                                                                        background: answers[question.question_id] === answer.answer_id ? "#eef6ff" : "#ffffff",
                                                                    }}
                                                                >
                                                                    {answer.answer_text}
                                                                </Radio>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </Radio.Group>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                            ))}

                            <Row gutter={[12, 12]} justify="end">
                                {canSkip && (
                                    <Col>
                                        <Button loading={surveyQuiz?.skipLoading} onClick={onSkip}>
                                            Skip
                                        </Button>
                                    </Col>
                                )}
                                <Col>
                                    <Button
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        disabled={!allAnswered}
                                        loading={surveyQuiz?.submitLoading}
                                        onClick={() => onSubmit(getQuizData())}
                                    >
                                        Submit
                                    </Button>
                                </Col>
                            </Row>
                        </Space>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default SurveyQuizBlock;
